import { z } from 'zod';
/**
 * Form Rules engine contract (README §6/§7). Shared so the rule builder validates
 * identically on client and server (README §10). The flexible per-rule payloads
 * (deadline, signoffObligation, condition.recurring) are JSONB, Zod-validated on write.
 */
// ── The ~23 trigger types (from the prototype's condTrigger options) ──
export const TRIGGER_TYPES = [
    'one_time',
    'specific_date',
    'admission',
    'enrollment',
    'discharge',
    'appointment',
    'offset_before',
    'offset_after',
    'appt_before',
    'appt_after',
    'date_field',
    'manual',
    'recurring',
    'auth_approval',
    'loc_change',
    'no_show',
    'form_complete',
    'med_change',
    'crisis_flag',
    'insurance_verify',
    'referral_received',
    'tx_plan_due',
    'staff_assign',
];
export const TRIGGER_TYPE_DEFS = [
    { value: 'one_time', label: 'One-time send' },
    { value: 'specific_date', label: 'On a specific date' },
    { value: 'admission', label: 'Upon admission' },
    { value: 'enrollment', label: 'Upon enrollment into program' },
    { value: 'discharge', label: 'Upon discharge / episode closure' },
    { value: 'appointment', label: 'On specific appointment type' },
    { value: 'offset_before', label: 'X days/hours before event' },
    { value: 'offset_after', label: 'X days/hours after event' },
    { value: 'appt_before', label: 'Before appointment type' },
    { value: 'appt_after', label: 'After appointment type' },
    { value: 'date_field', label: 'On a client date field' }, // ehr#257: client, not patient
    { value: 'manual', label: 'Manual staff trigger' },
    { value: 'recurring', label: 'Recurring (fixed interval)' },
    { value: 'auth_approval', label: 'Upon authorization approval' },
    { value: 'loc_change', label: 'Upon level of care change' },
    { value: 'no_show', label: 'Upon no-show / cancellation' },
    { value: 'form_complete', label: 'Upon form completion' },
    { value: 'med_change', label: 'Upon medication change' },
    { value: 'crisis_flag', label: 'Upon crisis flag' },
    { value: 'insurance_verify', label: 'Upon insurance verification' },
    { value: 'referral_received', label: 'Upon referral received' },
    { value: 'tx_plan_due', label: 'Upon treatment plan due / expiring' },
    { value: 'staff_assign', label: 'Upon staff assignment' },
];
export const offsetUnitSchema = z.enum(['minutes', 'hours', 'days', 'weeks', 'months']);
export const joinerSchema = z.enum(['AND', 'OR']);
export const ruleStatusSchema = z.enum(['active', 'paused', 'draft']);
export const conditionInputSchema = z.object({
    type: z.enum(TRIGGER_TYPES),
    event: z.string().max(100).nullish(),
    amount: z.number().int().nullish(),
    unit: offsetUnitSchema.nullish(),
    direction: z.enum(['before', 'after']).nullish(),
    recurring: z.record(z.unknown()).nullish(),
    joiner: joinerSchema.default('AND'),
    position: z.number().int().nonnegative().optional(),
});
export const filterInputSchema = z.object({
    dimension: z.enum(['contact_type', 'referral_source', 'provider', 'program']),
    value: z.string().min(1).max(120),
});
export const recipientInputSchema = z.object({
    kind: z.enum(['client', 'clinician', 'role', 'user']),
    value: z.string().max(120).nullish(),
});
export const deadlineSchema = z.object({
    enabled: z.boolean().default(false),
    days: z.number().int().min(0).default(0),
    hours: z.number().int().min(0).max(23).default(0),
    mins: z.number().int().min(0).max(59).default(0),
    remindDays: z.number().int().min(0).default(0),
    remindHours: z.number().int().min(0).max(23).default(0),
    remindMins: z.number().int().min(0).max(59).default(0),
});
export const signoffObligationSchema = z.object({
    enabled: z.boolean().default(false),
    remindEnabled: z.boolean().default(false),
    remindDays: z.number().int().min(0).default(1),
    remindHours: z.number().int().min(0).default(0),
    remindMins: z.number().int().min(0).default(0),
    escalateEnabled: z.boolean().default(false),
    escalateDays: z.number().int().min(0).default(3),
    escalateHours: z.number().int().min(0).default(0),
    escalateMins: z.number().int().min(0).default(0),
    supervisorSrc: z.enum(['org_chart', 'role', 'user']).default('org_chart'),
    supervisorRole: z.string().max(60).default(''),
    supervisorUser: z.string().max(120).default(''),
    chInApp: z.boolean().default(true),
    chEmail: z.boolean().default(true),
    chSecure: z.boolean().default(true),
});
export const ruleCreateSchema = z.object({
    name: z.string().min(1).max(200),
    status: ruleStatusSchema.default('draft'),
    formIds: z.array(z.string().uuid()).max(100).optional(),
    conditions: z.array(conditionInputSchema).max(50).optional(),
    filters: z.array(filterInputSchema).max(200).optional(),
    recipients: z.array(recipientInputSchema).max(50).optional(),
    deadline: deadlineSchema.nullish(),
    signoffObligation: signoffObligationSchema.nullish(),
});
export const ruleUpdateSchema = ruleCreateSchema.partial();
function phrase(c) {
    switch (c.type) {
        case 'admission':
            return c.event ? `Upon admission → ${c.event}` : 'Upon admission';
        case 'enrollment':
            return c.event ? `Upon enrollment → ${c.event}` : 'Upon enrollment';
        case 'discharge':
            return 'Upon discharge';
        case 'offset_before':
            return `${c.amount ?? '?'} ${c.unit ?? ''} before ${c.event ?? 'event'}`.replace(/\s+/g, ' ').trim();
        case 'offset_after':
            return `${c.amount ?? '?'} ${c.unit ?? ''} after ${c.event ?? 'event'}`.replace(/\s+/g, ' ').trim();
        case 'recurring':
            return `Every ${c.amount ?? '?'} ${c.unit ?? ''} (recurring)`.replace(/\s+/g, ' ').trim();
        case 'one_time':
            return 'One-time send';
        case 'specific_date':
            return 'On a specific date';
        case 'manual':
            return 'Manual staff trigger';
        default: {
            const def = TRIGGER_TYPE_DEFS.find((d) => d.value === c.type);
            return def ? def.label : c.type;
        }
    }
}
export function summarizeConditions(conditions) {
    if (!conditions.length)
        return 'No trigger';
    return conditions
        .map((c, i) => (i > 0 ? ` ${c.joiner ?? 'AND'} ` : '') + phrase(c))
        .join('');
}
//# sourceMappingURL=rules.js.map